import '@iotize/tap/service/impl/data-log';

import { Decoder } from '@iotize/common/converter/api';
import { isCodeError } from '@iotize/common/error';
import { ProgressState } from '@iotize/common/progress/api';
import { TLV } from '@iotize/common/tlv';
import { Tap, TapError, TapResponseStatusError } from '@iotize/tap';
import { ResultCode } from '@iotize/tap/client/api';
import { TlvBundleConverter, TLVConverter } from '@iotize/tap/client/impl';
import { SinglePacket } from '@iotize/tap/service/impl/single-packet';
import { interval, Observable } from 'rxjs';
import { map, share, switchMap } from 'rxjs/operators';

import { DataLoggerError } from './errors';

const TAG = 'DataLogger';
/**
 * Experimental data logger class
 *
 * @experimental
 */
export class DataLogger {
  constructor(
    private tap: Tap,
    public converter = new DataLogSinglePacketToDataLogPacketConverter(),
  ) {}

  // public static fromTap(tap: Tap, options: DataLogger.Options) {
  //     const converter = DataLogPacketConverter.createFromManager(
  //         tap.bundles,
  //         tap.variables
  //     );
  //     return new DataLogger(tap, converter, options);
  // }

  async isAcquisitionRunning(): Promise<boolean> {
    return (await this.tap.service.dataLog.isRunning()).body();
  }

  async startAcquisition() {
    try {
      (await this.tap.service.dataLog.run()).successful();
    } catch (err) {
      if (
        isCodeError<TapResponseStatusError>(
          TapError.Code.ResponseStatusError,
          err,
        )
      ) {
        const statusCode = err.response.status;
        if (statusCode === ResultCode.UNAUTHORIZED) {
          throw DataLoggerError.notAuthorizedToPerformAction(
            `You are not authorized to start data log acquisition. Login to perform this action`,
            err,
          );
        }
      }
      throw err;
    }
  }

  async stopAcquisition() {
    try {
      (await this.tap.service.dataLog.stop()).successful();
    } catch (err) {
      if (
        isCodeError<TapResponseStatusError>(
          TapError.Code.ResponseStatusError,
          err,
        )
      ) {
        const statusCode = err.response.status;
        if (statusCode === ResultCode.UNAUTHORIZED) {
          throw DataLoggerError.notAuthorizedToPerformAction(
            `You are not authorized to stop data log acquisition. Login to perform this action`,
            err,
          );
        }
      }
      throw err;
    }
  }

  /**
   * Dequeue one data log packet
   */
  async dequeue(): Promise<DataLogger.RawPacket> {
    const singlePacketResponse =
      await this.tap.service.dataLog.dequeueOnePacket();
    const singlePacket = singlePacketResponse.body();
    // singlePacketResponse.successful();
    // const singlePacketFrame = singlePacketResponse.rawBody();
    try {
      return this.converter.decode(singlePacket);
    } catch (err) {
      throw DataLoggerError.decodePacketError(singlePacket, err as Error);
    }
  }

  /**
   * Get stored packet count in Tap
   */
  async getPacketCount(): Promise<number> {
    return (await this.tap.service.dataLog.getPacketCount()).body();
  }

  /**
   * Download all packets from store
   */
  download(options?: {
    period?: number;
  }): Observable<DataLogger.DownloadEvent> {
    return interval(options?.period || 400).pipe(
      switchMap((time) => this.dequeue()),
      map(
        (
          datalogPacket: DataLogger.RawPacket,
          index: number,
        ): DataLogger.DownloadEvent => {
          try {
            return {
              type: 'new-packet',
              progress: {
                total: undefined,
                loaded: index + 1,
              },
              packet: datalogPacket,
            };
          } catch (err) {
            return {
              type: 'error',
              error: err as Error,
            };
          }
        },
      ),
      share(),
    );
  }
}

export namespace DataLogger {
  export interface Options {
    period: number;
  }

  export type DownloadEvent =
    | {
        type: 'new-packet';
        progress: ProgressState;
        packet: RawPacket;
      }
    | {
        type: 'error';
        error: Error;
      };

  /**
   * Data log packet containing raw values for each variables
   */
  export interface RawPacket {
    // _raw?: Uint8Array;
    /**
     * Bundle infor
     */
    meta: RawPacket.Meta;
    /**
     * Raw variable data mapping between a variable identifier and the raw data
     */
    data: RawPacket.Data;
  }

  export namespace RawPacket {
    export interface Meta {
      bundleId: number;
      logTime: Date;
      /**
       * Single packet indentifier
       */
      packetId: number;
      /**
       * Single packet sender identifier
       */
      senderId: number;
    }

    export type Data = Record<number, Uint8Array>;
  }

  // export interface Packet<T extends Record<string, any>> extends RawPacket {
  //     bundle: {
  //         id: number;
  //         logTime?: Date;
  //         name?: string;
  //     };
  //     variables: T;
  //     // logTime: Date,
  //     // singlePacket?: SinglePacket
  // }

  // export interface PacketInfo<T = Record<string, any>> {
  //     singlePacket: SinglePacket;
  //     rawSinglePacket: Uint8Array;
  //     packet: Packet<T>;
  // }
}

/**
 *
 * @experimental
 */
export class DataLogSinglePacketToDataLogPacketConverter
  implements Decoder<SinglePacket, DataLogger.RawPacket>
{
  constructor(
    public tlvConverter = new TLVConverter(),
    public bundleConverter = new TlvBundleConverter(), // public singlePacketConverter = new SinglePacketConverter()
  ) {}

  // encode(data: SinglePacket): DataLogger.RawPacket {
  //     throw new Error('Method not implemented.');
  // }

  decode(singlePacket: SinglePacket): DataLogger.RawPacket {
    const data = singlePacket.payload.data;
    const tlvTree: TLV.TlvNode = this.tlvConverter.decode(data);
    const bundleId = tlvTree.header.id;
    // console.log(tlvTree);
    const variables = tlvTree.children
      ? this.bundleConverter.decode(tlvTree)
      : {};

    const timeOffset = Date.now() - singlePacket.sendTime * 1000;
    const logTime = new Date(singlePacket.payload.logTime * 1000 + timeOffset);

    return {
      meta: {
        packetId: singlePacket.header.packetId,
        senderId: singlePacket.header.senderId,
        bundleId,
        logTime,
      },
      data: variables,
    };
  }
}

/**
 *
 * @experimental
 */
// export class DataLogPacketConverter<T = Record<string, any>>
//     implements
//     EncoderDecoder<
//     DataLogger.PacketInfo<T>,
//     { raw: Uint8Array; singlePacket: SinglePacket }
//     > {
//     constructor(
//         public meta?: BundleConfig[],
//         public rawPacketConverter: RawPacketConverter = new RawPacketConverter()
//     ) { }

//     public static createFromMeta<T>(
//         meta: BundleConfig[],
//         rawPacketConverter: RawPacketConverter = new RawPacketConverter()
//     ): DataLogPacketConverter<T> {
//         return new DataLogPacketConverter(meta, rawPacketConverter);
//     }

//     encode(
//         type: DataLogger.PacketInfo<T>
//     ): { raw: Uint8Array; singlePacket: SinglePacket } {
//         throw new Error('Method not implemented.');
//     }

//     decode(data: {
//         raw: Uint8Array;
//         singlePacket: SinglePacket;
//     }): DataLogger.PacketInfo<T> {
//         const singlePacket = data.singlePacket;
//         const dataLogPacket = this.rawPacketConverter.decode(
//             singlePacket.payload.data
//         );
//         const timeOffset = Date.now() - singlePacket.sendTime * 1000;
//         dataLogPacket.bundle.logTime = new Date(
//             singlePacket.payload.logTime * 1000 + timeOffset
//         );
//         const packet = populateDataLogPacket<T>(dataLogPacket, this.meta || []);
//         return {
//             packet,
//             rawSinglePacket: data.raw,
//             singlePacket
//         };
//     }
// }
